import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Download, Layers, AlertCircle, BookOpen, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const BAND_COLOR = {
  'Entering/Emerging': 'border-rose-300 bg-rose-50',
  'Developing/Expanding': 'border-amber-300 bg-amber-50',
  'Bridging/Reaching': 'border-emerald-300 bg-emerald-50',
};

const LEVEL_BADGE = {
  1: 'bg-rose-100 text-rose-800 border-rose-300',
  2: 'bg-rose-100 text-rose-800 border-rose-300',
  3: 'bg-amber-100 text-amber-800 border-amber-300',
  4: 'bg-amber-100 text-amber-800 border-amber-300',
  5: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  6: 'bg-emerald-100 text-emerald-800 border-emerald-300',
};

export default function ClassGroupsReport() {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [programs, setPrograms] = useState([]);
  const [programId, setProgramId] = useState(localStorage.getItem('mosaic_default_crosswalk_program') || '');
  const [modality, setModality] = useState('reading');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/classes`, { headers: authHeader() })
      .then(r => setClasses(r.data.classes || []))
      .catch(() => {});
    axios.get(`${API}/crosswalk/programs`, { headers: authHeader() })
      .then(r => setPrograms(r.data.programs || []))
      .catch(() => {});
  }, []);

  const handleLoad = async () => {
    if (!classId || !programId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/crosswalk/class-groups/${classId}`, {
        headers: authHeader(),
        params: { program_id: programId, modality },
      });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePdf = async () => {
    if (!classId || !programId) return;
    const token = localStorage.getItem('session_token');
    const r = await fetch(`${API}/crosswalk/class-groups-pdf/${classId}?program_id=${programId}&modality=${modality}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `MOSAIC_Class_Groups_${classId}_${programId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="class-groups-report">
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
            <Users className="text-purple-600" size={22} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Whole-Class Crosswalk Groups</h2>
            <p className="text-sm text-gray-600 mt-1">
              Group your students by proficiency level for differentiated small-group instruction. Each group gets matched units, scaffolds, and sentence frames.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mt-5">
          <div>
            <label className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1 block">Class</label>
            <select value={classId} onChange={e => setClassId(e.target.value)} data-testid="cg-class-select" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
              <option value="">Choose a class…</option>
              {classes.map(c => <option key={c.class_id} value={c.class_id}>{c.name} ({c.grade_level || '?'})</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1 block">Program</label>
            <select value={programId} onChange={e => { setProgramId(e.target.value); if (e.target.value) localStorage.setItem('mosaic_default_crosswalk_program', e.target.value); }} data-testid="cg-program-select" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
              <option value="">Choose program…</option>
              {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1 block">Modality</label>
            <select value={modality} onChange={e => setModality(e.target.value)} data-testid="cg-modality-select" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
              <option value="reading">Reading</option>
              <option value="listening">Listening</option>
              <option value="speaking">Speaking</option>
              <option value="writing">Writing</option>
            </select>
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={handleLoad} disabled={!classId || !programId || loading} className="flex-1" data-testid="cg-load-btn">
              <Wand2 size={16} className="mr-1" />
              {loading ? 'Grouping…' : 'Build Groups'}
            </Button>
            <Button onClick={handlePdf} disabled={!classId || !programId} variant="outline" title="Download printable small-group plan" data-testid="cg-pdf-btn">
              <Download size={16} />
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start gap-2" data-testid="cg-error">
          <AlertCircle size={18} className="mt-0.5"/> <span>{error}</span>
        </div>
      )}

      {data && (
        <>
          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm" data-testid="cg-summary">
            <div className="flex flex-wrap items-center gap-3">
              <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-bold uppercase tracking-wider">
                {data.class_name}
              </span>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold">
                {data.program.name}
              </span>
              <span className="text-sm text-gray-600">
                {data.assessed_students} / {data.total_students} students assessed · Grade {data.grade_used} · {data.modality}
              </span>
            </div>
            {data.unassessed_students?.length > 0 && (
              <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                <strong>Unassessed ({data.unassessed_students.length}):</strong> {data.unassessed_students.map(s => s.name).join(', ')} — administer an exam to assign these students to a group.
              </div>
            )}
          </div>

          {data.groups.length === 0 ? (
            <div className="p-8 text-center text-gray-500 bg-white border border-gray-200 rounded-lg">
              No assessed students in this class yet.
            </div>
          ) : (
            <div className="space-y-4">
              {data.groups.map(g => (
                <div key={g.wida_level} className={`border-2 ${BAND_COLOR[g.band] || ''} rounded-lg p-5`} data-testid={`cg-group-${g.wida_level}`}>
                  <div className="flex items-start gap-3 flex-wrap mb-3">
                    <div className={`px-3 py-1 rounded-full border text-sm font-bold ${LEVEL_BADGE[g.wida_level]}`}>
                      Level {g.wida_level} · {g.wida_level_name}
                    </div>
                    <div className="text-xs text-gray-600 uppercase tracking-wider font-semibold">
                      {g.band} · {g.student_count} student{g.student_count !== 1 ? 's' : ''}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {g.students.map(s => (
                      <span key={s.student_id} className="bg-white border border-gray-300 rounded-full px-3 py-1 text-xs font-medium text-gray-800" data-testid={`cg-student-${s.student_id}`}>
                        {s.name} <span className="text-gray-400">({s.wida_level_float})</span>
                      </span>
                    ))}
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="bg-white border border-gray-200 rounded p-3">
                      <div className="text-[10px] uppercase font-bold text-blue-700 mb-1 flex items-center gap-1"><BookOpen size={10}/> Units</div>
                      <ul className="text-xs text-gray-700 space-y-0.5">
                        {g.recommended_units.map(u => (
                          <li key={u.id}><strong>{u.name}</strong></li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-white border border-gray-200 rounded p-3">
                      <div className="text-[10px] uppercase font-bold text-purple-700 mb-1">Sentence Frames</div>
                      <ul className="text-xs text-gray-800 space-y-1 font-mono">
                        {g.sentence_frames.map((f, i) => <li key={i}>• {f}</li>)}
                      </ul>
                    </div>
                    <div className="bg-white border border-gray-200 rounded p-3">
                      <div className="text-[10px] uppercase font-bold text-emerald-700 mb-1">Scaffolds</div>
                      <div className="text-xs text-gray-700 space-y-0.5">
                        <div><strong>Sensory:</strong> {g.scaffolds.sensory}</div>
                        <div><strong>Graphic:</strong> {g.scaffolds.graphic}</div>
                        <div><strong>Interactive:</strong> {g.scaffolds.interactive}</div>
                      </div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded p-3">
                      <div className="text-[10px] uppercase font-bold text-amber-700 mb-1">Vocabulary Focus</div>
                      <div className="text-xs text-gray-700">{g.vocabulary_focus}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
